import ArrowRightOutlined from "@ant-design/icons/ArrowRightOutlined";
import BranchesOutlined from "@ant-design/icons/BranchesOutlined";
import EditOutlined from "@ant-design/icons/EditOutlined";
import EllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import StopOutlined from "@ant-design/icons/StopOutlined";
import React, { useContext, useState } from "react";
import styled, { useTheme } from "styled-components";
import { TargetModal } from "features/signals/EditSignal/TargetModal";
import { EditSignalContext } from "features/signals/EditSignal/EditSignal.context";
import { getTargetsBelowPrice } from "features/signals/EditSignal/utils";
import { ErrorTypes, OrderTypes } from "@shared/features/trades/common/types";
import { LanguageNameSpaces, OrderStatus } from "@shared/types";
import { openMessage } from "utils/misc";
import { ORDER_TYPE_TO_TITLE_DISPLAY } from "@shared/common/constants";
import {
  getErrorDisplayMessage,
  orderHasStatus,
} from "@shared/features/trades/common/utils";
import {
  isEditableTargetZone,
  isTargetZone,
} from "@shared/features/signals/common/utils";
import { EditTargetOrZone } from "@shared/features/signals/EditSignal/types";
import {
  getTargetsBreakEvenPrice,
  isPartiallyFulfilledZone,
} from "@shared/features/signals/EditSignal/utils";
import { validateOrderRelativePrice } from "@shared/features/tradingTerminal/common/utils";
import Dropdown from "common/Dropdown/Dropdown";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { joinWords } from "@shared/common/i18n/utils.ts";

interface EditTargetActionsProps {
  targetType: OrderTypes;
  targetIndex: number;
  target: EditTargetOrZone;
}

const EditTargetActions = ({
  targetType,
  targetIndex,
  target,
}: EditTargetActionsProps) => {
  const { t: tTrades } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { t: tTradingTerminal } = useTypedTranslation(
    LanguageNameSpaces.TRADING_TERMINAL
  );
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const getMoveToBreakEvenPriceErrors = () => {
    if (!breakEvenPrice)
      return [ErrorTypes.CANNOT_BREAK_EVEN_UNFULFILLED_TARGETS];
    if (targetType === OrderTypes.STOP)
      return validateOrderRelativePrice(
        breakEvenPrice,
        targetType,
        !!isShort,
        !!signalData?.breakout,
        currentPrice,
        [],
        [],
        false
      );

    return [];
  };

  const appTheme = useTheme();
  const {
    getTargetsByType,
    cancelTarget,
    moveTargetToCurrentPrice,
    moveStopTargetToBreakEvenPrice,
    entryTargets,
    takeProfitTargets,
    confirmWithCallback,
    currentPrice,
    signalData,
    setTargetsByType,
    updateTarget,
  } = useContext(EditSignalContext)!;
  const [editTargetModalOpen, setEditTargetModalOpen] = useState(false);

  const onEditTargetClicked = () => {
    if (
      target.edit_status === OrderStatus.PENDING ||
      isPartiallyFulfilledZone(target)
    )
      setEditTargetModalOpen(true);
    else {
      openMessage(
        `Cannot edit a ${target.edit_status.toLowerCase()} ${
          isEditableTargetZone(target) ? "zone" : "target"
        }`,
        "warning"
      );
    }
  };
  const isShort = signalData?.reverse;
  const moveToCurrentPriceErrors =
    targetType !== OrderTypes.STOP
      ? validateOrderRelativePrice(
          currentPrice,
          targetType,
          !!isShort,
          !!signalData?.breakout,
          currentPrice,
          entryTargets.filter((target) =>
            orderHasStatus(target, true, OrderStatus.PENDING)
          ),
          takeProfitTargets.filter((target) =>
            orderHasStatus(target, true, OrderStatus.PENDING)
          )
        )
      : [];

  const breakEvenPrice = getTargetsBreakEvenPrice(entryTargets, isShort);
  const targetsBelowPrice = getTargetsBelowPrice(
    entryTargets,
    breakEvenPrice,
    isShort
  );
  const moveToBreakEvenPriceErrors = getMoveToBreakEvenPriceErrors();
  const onMoveToCurrentPriceClick = () => {
    if (moveToCurrentPriceErrors.length > 0) {
      openMessage(
        getErrorDisplayMessage(
          tTrades,
          moveToCurrentPriceErrors[0],
          targetType,
          true
        ),
        "warning"
      );
    } else {
      moveTargetToCurrentPrice(targetType, targetIndex);
    }
  };

  const onMoveToBreakEvenPriceClick = async () => {
    if (moveToBreakEvenPriceErrors.length > 0) {
      openMessage(
        getErrorDisplayMessage(
          tTrades,
          moveToBreakEvenPriceErrors[0],
          targetType,
          true
        ),
        "warning"
      );
      return;
    }

    if (targetsBelowPrice.length === 0) {
      moveStopTargetToBreakEvenPrice(targetsBelowPrice);
      return;
    }

    const okPressed = await confirmWithCallback(
      t("edit_signal.actions.moving_stop_loss_to_break_even", {
        breakEvenPrice,
        numTargetsBelowPrice: targetsBelowPrice.length,
        items:
          targetsBelowPrice.length > 1
            ? t("edit_signal.actions.entries")
            : t("edit_signal.actions.entry"),
        belowOrAbove: !isShort
          ? t("edit_signal.actions.below")
          : t("edit_signal.actions.above"),
      })
    );
    if (okPressed) moveStopTargetToBreakEvenPrice(targetsBelowPrice);
  };

  const onCancelClick = (shouldRedistribute: boolean) => {
    const warningMessage = getCancelTargetWarningMessage(shouldRedistribute);
    if (warningMessage) openMessage(warningMessage, "warning");
    else {
      const allTargets = getTargetsByType(targetType);
      const updatedTargets = cancelTarget(
        targetType,
        targetIndex,
        shouldRedistribute,
        allTargets
      );
      setTargetsByType(targetType, updatedTargets);
    }
  };

  const hasSiblingTargets = (includeFulfilled?: boolean) => {
    return getTargetsByType(targetType).some((currentTarget) => {
      const isCancelled = currentTarget.edit_status === OrderStatus.CANCELLED;
      const isFulfilled =
        includeFulfilled && currentTarget.status === OrderStatus.FULFILLED;
      const sameTarget = currentTarget === target;
      return !isCancelled && !isFulfilled && !sameTarget;
    });
  };

  const getCancelTargetWarningMessage = (
    shouldRedistribute?: boolean
  ): string => {
    if (target.edit_status === OrderStatus.CANCELLED)
      return t("edit_signal.errors.cannot_redistribute_canceled");

    if (shouldRedistribute) {
      if (!hasSiblingTargets(false))
        return getErrorDisplayMessage(
          tTrades,
          ErrorTypes.CANT_REDISTRIBUTE_NO_SIBLINGS_TARGETS
        )!;
      else if (!isEditableTargetZone(target) && !target.edit_ratio)
        return t("edit_signal.errors.cannot_redistribute_no_ratio");
    } else {
      if (
        targetType === OrderTypes.ENTRY &&
        !hasSiblingTargets(true) &&
        isEditableTargetZone(target) &&
        target.status === OrderStatus.PENDING
      )
        return t("edit_signal.actions.cancelling_zone_or_target", {
          item: (isEditableTargetZone(target)
            ? t("edit_signal.actions.zone")
            : t("edit_signal.actions.target")
          ).toLowerCase(),
        });
    }
    return "";
  };

  let menuItems = [
    {
      key: "edit",
      icon: <EditOutlined />,
      label: t("edit_signal.actions.edit"),
      onClick: onEditTargetClicked,
      className:
        target.edit_status === OrderStatus.CANCELLED ||
        !(
          target.status === OrderStatus.PENDING ||
          isPartiallyFulfilledZone(target)
        )
          ? "invalid"
          : undefined,
    },
    {
      key: "cancel",
      icon: <StopOutlined />,
      label: t("edit_signal.actions.cancel"),
      onClick: () => onCancelClick(false),
      className: Boolean(getCancelTargetWarningMessage())
        ? "invalid"
        : undefined,
    },
  ];

  if (!isEditableTargetZone(target)) {
    if (targetType === OrderTypes.STOP) {
      menuItems.push({
        key: "move",
        icon: <ArrowRightOutlined />,
        label: t("edit_signal.actions.move_to_breakeven"),
        onClick: onMoveToBreakEvenPriceClick,
        className:
          moveToBreakEvenPriceErrors.length > 0 ? "invalid" : undefined,
      });
    } else {
      menuItems = menuItems.concat(
        ...[
          {
            key: "cancelAndRedistribute",
            icon: <BranchesOutlined />,
            label: t("edit_signal.actions.cancel_and_redistribute"),
            onClick: () => onCancelClick(true),
            className: Boolean(getCancelTargetWarningMessage(true))
              ? "invalid"
              : undefined,
          },
          {
            key: "move",
            icon: <ArrowRightOutlined />,
            label: t("edit_signal.actions.move_to_current"),
            onClick: onMoveToCurrentPriceClick,
            className:
              moveToCurrentPriceErrors.length > 0 ? "invalid" : undefined,
          },
        ]
      );
    }
  }

  const onUpdateTarget = (updatedTarget: EditTargetOrZone) =>
    updateTarget(targetType, targetIndex, updatedTarget);
  const wrapperId = `${targetType}_${targetIndex}_actions`;

  const getTitle = () => {
    const isZone = isTargetZone(target);
    const type = isZone
      ? t("edit_signal.actions.zone")
      : t("edit_signal.actions.target");
    const suffix = isZone ? "" : `#${targetIndex + 1}`;
    return joinWords([
      t("edit_signal.actions.edit"),
      tTradingTerminal(ORDER_TYPE_TO_TITLE_DISPLAY[targetType]),
      type,
      suffix,
    ]);
  };

  return (
    <Wrapper id={wrapperId}>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Dropdown
          title={t("edit_signal.actions.target_actions")}
          menu={{ items: menuItems }}
          overlayStyle={{ userSelect: "none", width: "max-content" }}
          trigger={["click"]}
          getPopupContainer={() => document.getElementById(wrapperId)!}
        >
          <EllipsisOutlined
            style={{
              fontSize: 24,
              cursor: "pointer",
              color: appTheme.colors.basic,
            }}
          />
        </Dropdown>
      </div>
      {editTargetModalOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <TargetModal
            closeModal={() => setEditTargetModalOpen(false)}
            title={getTitle()}
            targetType={targetType}
            target={{ ...target }}
            onOk={onUpdateTarget}
          />
        </div>
      )}
    </Wrapper>
  );
};

export default EditTargetActions;

const Wrapper = styled.div`
  position: absolute;
  right: 16px;
`;
